/**
 * AuditApp — the centralized Audit & Monitoring tool.
 *
 * One tool for the whole plan. Internal Audit & Monitoring audits internal
 * departments/processes; the FDR team audits vendors (scope="fdr"). Same engine,
 * same template library, same grounded reviewer. End-to-end lifecycle:
 * setup (subject + authorities + audit program) -> engagement letter ->
 * data integrity (when applicable) -> fieldwork (grounded review) ->
 * reporting -> findings & CAP -> close.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PlanView from './PlanView.jsx';
import FdrPlanView from '../FDR/FdrPlanView.jsx';
import ReportingView from './ReportingView.jsx';
import CopilotPanel from './CopilotPanel.jsx';
import IntakeDrawer from './IntakeDrawer.jsx';
import WorkflowBar from './WorkflowBar.jsx';
import PhiNotice from './PhiNotice.jsx';
import ActivityTrail from './ActivityTrail.jsx';
import PhaseSection from './PhaseSection.jsx';
import PhaseRail from './PhaseRail.jsx';
import SamplesPanel from './SamplesPanel.jsx';
import ReportCyclePanel from './ReportCyclePanel.jsx';
import { UniverseAttemptsPanel } from './EngagementPanels.jsx';
import { BinderPanel, PhiUploadNotice, binderFolders } from './BinderPanel.jsx';
import { ConferencesPanel, AnalysisRequestsPanel } from './FieldworkPanels.jsx';
import { CorrespondenceCard, ComposeDialog } from './CorrespondencePanel.jsx';
import AuditeeAccessPanel from './AuditeeAccessPanel.jsx';
import AuditTeamPanel from './AuditTeamPanel.jsx';
import MyAudits from './MyAudits.jsx';
import ProgramAudits from './ProgramAudits.jsx';
import { STAGE_LABELS, FINDING_STATUS_LABELS, REPORT_KIND_LABELS, VALIDATION_METHOD_LABELS } from './labels.js';

const API = process.env.REACT_APP_API_URL;
const RUNNING = new Set(['reviewing', 'scoring']);

// Dates come back from the API as ISO timestamps; show them as dates.
function fmtHeaderDate(v) {
  if (!v) return '';
  const m = String(v).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(v);
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]))
    .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

// The six internal stages, mapped onto the four phases of the CMS Routine
// Program Audit process that frame the workspace.
const PHASE_OF_STAGE = { setup: 1, engagement: 1, data_integrity: 1, fieldwork: 2, reporting: 3, closed: 4 };

const STATUS_CFG = {
  met:                 { label: 'Met',          dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
  deficient:           { label: 'Deficient',    dot: 'bg-amber-500',   text: 'text-amber-700',   bg: 'bg-amber-50 border-amber-200' },
  missing:             { label: 'Missing',      dot: 'bg-red-500',     text: 'text-red-700',     bg: 'bg-red-50 border-red-200' },
  not_applicable:      { label: 'N/A',          dot: 'bg-slate-300',   text: 'text-slate-500',   bg: 'bg-slate-50 border-slate-200' },
  unable_to_determine: { label: 'Needs review', dot: 'bg-indigo-400',  text: 'text-indigo-600',  bg: 'bg-indigo-50 border-indigo-200' },
};
const SEV_CFG = { critical: 'text-red-700 bg-red-100', high: 'text-orange-700 bg-orange-100', medium: 'text-amber-700 bg-amber-100', low: 'text-slate-600 bg-slate-100' };
const POSTURE_CFG = {
  compliant: { label: 'Compliant', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  minor_deficiencies: { label: 'Minor deficiencies', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  material_gaps: { label: 'Material gaps', color: 'bg-red-50 text-red-700 border-red-200' },
  not_assessable: { label: 'Not assessable', color: 'bg-slate-50 text-slate-600 border-slate-200' },
};
const STATUS_OPTS = [['met', 'Met'], ['deficient', 'Deficient'], ['missing', 'Missing'], ['not_applicable', 'N/A'], ['unable_to_determine', 'Needs review']];
// CMS condition classifications, with the one-line definition each pill
// carries as its tooltip.
const CLASS_DEFS = [
  ['observation', 'Observation', 'A deficiency noted in the report that needs no corrective action plan, including issues the auditee disclosed and already corrected.'],
  ['car', 'CAR', 'Corrective Action Required: a condition the auditee must fix through a corrective action plan, then validate before close out.'],
  ['ids', 'IDS', 'Invalid Data Submission: cited when a universe still cannot be tested after three submission attempts.'],
];
const AUTH_KINDS = [['cfr', 'CFR'], ['chapter', 'CMS Chapter'], ['ncqa', 'NCQA'], ['state', 'State contract'], ['policy', 'Internal policy'], ['other', 'Other']];

function AuthoritiesEditor({ value, onChange }) {
  const [kind, setKind] = useState('cfr');
  const [name, setName] = useState('');
  const add = () => { if (!name.trim()) return; onChange([...(value || []), { kind, name: name.trim() }]); setName(''); };
  return (
    <div>
      <div className="text-[11px] font-semibold text-slate-500 mb-1">Controlling authorities (grounds the review in the exact rules that apply)</div>
      <div className="flex flex-wrap gap-1 mb-2">
        {(value || []).map((a, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-slate-100 text-slate-700 rounded-full px-2 py-0.5">
            <span className="text-slate-400">{a.kind}</span> {a.name}
            <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600">×</button>
          </span>
        ))}
        {!(value || []).length && <span className="text-[11px] text-slate-400">None yet. Add the CFR parts, CMS chapter names, NCQA or state standards that govern this audit.</span>}
      </div>
      <div className="flex gap-1">
        <select value={kind} onChange={e => setKind(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700">
          {AUTH_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} placeholder="e.g., 42 CFR §422.204 or Chapter 21" className="flex-1 text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
        <button onClick={add} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">Add</button>
      </div>
    </div>
  );
}

function CriterionRow({ r, auditId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const cfg = STATUS_CFG[r.status] || STATUS_CFG.unable_to_determine;
  const override = async (status) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/audit/audits/${auditId}/criteria/${r.id}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
      const d = await res.json(); if (d.success) onChanged();
    } catch {}
    setSaving(false);
  };
  return (
    <div className={`rounded-xl border ${cfg.bg} overflow-hidden`}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-start gap-3 px-4 py-3 text-left">
        <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 text-sm">{r.criterion_title}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${SEV_CFG[r.severity] || SEV_CFG.low}`}>{r.severity}</span>
            <span className={`text-[11px] font-semibold ${cfg.text}`}>{cfg.label}</span>
            {r.verified && <span className="text-[10px] text-emerald-600">✓ verified</span>}
            {r.needs_human && !r.human_overridden && <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5">Awaiting your review</span>}
            {r.human_overridden && <span className="text-[10px] text-slate-500">reviewer</span>}
            {r.finding_id && <span className="text-[10px] text-orange-600">→ finding</span>}
          </div>
          {r.citation && <div className="text-[11px] font-mono text-slate-400 mt-0.5">{r.citation}</div>}
        </div>
        <svg className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="px-4 pb-4 pl-9 space-y-2 text-sm">
          {r.analysis && <p className="text-slate-700">{r.analysis}</p>}
          {r.authority_text && <div><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Controlling authority</div><p className="text-[12px] text-slate-500">{r.authority_text}</p></div>}
          {r.evidence_quote && <div><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Evidence (verbatim{r.evidence_location ? ` · ${r.evidence_location}` : ''})</div><blockquote className="border-l-2 border-slate-300 pl-3 italic text-slate-600 text-[13px]">{r.evidence_quote}</blockquote></div>}
          {r.absence_rationale && !r.evidence_quote && <div><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Why concluded missing</div><p className="text-[12px] text-slate-500">{r.absence_rationale}</p></div>}
          {r.recommended_action && <div><div className="text-[10px] font-semibold uppercase tracking-wide text-orange-500 mb-1">Recommended corrective action</div><div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-[13px] text-slate-700">{r.recommended_action}</div></div>}
          <div className="flex items-center gap-2 pt-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">Reviewer override</span>
            <select disabled={saving} defaultValue={r.status} onChange={e => override(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-2 py-1 text-slate-700">
              {STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

function FindingCard({ f, auditId, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [owner, setOwner] = useState(f.cap_owner || '');
  const [due, setDue] = useState(f.cap_due_date ? f.cap_due_date.slice(0, 10) : '');
  const [capDesc, setCapDesc] = useState(f.cap_description || '');
  const [status, setStatus] = useState(f.status || 'cap_required');
  const [classifying, setClassifying] = useState(false);
  const save = async () => {
    try {
      const res = await fetch(`${API}/api/audit/audits/${auditId}/findings/${f.id}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cap_owner: owner, cap_due_date: due || null, cap_description: capDesc, status }) });
      const d = await res.json(); if (d.success) { setEdit(false); onChanged(); }
    } catch {}
  };
  // CMS classification (Observation / CAR / IDS) and, for a CAR, the
  // validation method — both via the classification endpoint.
  const classify = async (classification, method) => {
    setClassifying(true);
    try {
      const body = { classification };
      if (method) body.validation_method = method;
      const res = await fetch(`${API}/api/audit/audits/${auditId}/findings/${f.id}/classification`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const d = await res.json(); if (d.success) onChanged();
    } catch {}
    setClassifying(false);
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${SEV_CFG[f.severity] || SEV_CFG.low}`}>{f.severity}</span>
        <span className="text-sm font-semibold text-slate-800">{f.title}</span>
        <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{FINDING_STATUS_LABELS[f.status] || f.status}</span>
        {f.fdr_finding_id && <span className="text-[10px] text-orange-500">→ FDR Findings</span>}
        <button onClick={() => setEdit(e => !e)} className="ml-auto text-[11px] text-orange-600">{edit ? 'Cancel' : 'CAP'}</button>
      </div>
      {f.recommended_action && <div className="text-[12px] text-slate-600 mt-1">{f.recommended_action}</div>}
      <div className="flex items-center gap-2 flex-wrap mt-2">
        <span className="text-[10px] uppercase tracking-wide text-slate-400">CMS classification</span>
        {CLASS_DEFS.map(([v, label, def]) => (
          <button
            key={v} type="button" title={def} disabled={classifying}
            onClick={() => classify(v, v === 'car' ? (f.validation_method || undefined) : undefined)}
            className={`text-[11px] font-semibold rounded-full px-2.5 py-0.5 border ${f.classification === v ? 'bg-orange-100 text-orange-700 border-orange-300' : 'bg-white text-slate-500 border-slate-200 hover:border-orange-200'}`}
          >{label}</button>
        ))}
        {f.classification === 'car' && (
          <select value={f.validation_method || ''} disabled={classifying} onChange={e => classify('car', e.target.value || undefined)} className="text-xs border border-slate-300 rounded-lg px-2 py-1 text-slate-700">
            <option value="">Validation method…</option>
            {Object.entries(VALIDATION_METHOD_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        )}
      </div>
      {f.classification === 'car' && (
        <div className="text-[11px] text-slate-500 mt-1">
          {f.validated_at
            ? <>Validated {new Date(f.validated_at).toLocaleDateString()}{f.validation_notes ? ` · ${f.validation_notes}` : ''}</>
            : 'Not validated yet — validation is confirmed in Phase IV before close out.'}
        </div>
      )}
      {edit && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          <input value={owner} onChange={e => setOwner(e.target.value)} placeholder="CAP owner" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
          <input type="date" value={due} onChange={e => setDue(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
          <textarea value={capDesc} onChange={e => setCapDesc(e.target.value)} rows={2} placeholder="Corrective action plan" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 col-span-2" />
          <select value={status} onChange={e => setStatus(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700">
            {['open', 'cap_required', 'cap_in_progress', 'remediated', 'closed'].map(s => <option key={s} value={s}>{FINDING_STATUS_LABELS[s] || s}</option>)}
          </select>
          <button onClick={save} className="text-xs font-semibold rounded-lg bg-emerald-600 text-white px-3 py-1.5">Save CAP</button>
        </div>
      )}
    </div>
  );
}

// ── Phase IV: validation of corrective actions before close out ──
// The validation method is set through the classification endpoint; the
// validated timestamp and notes are recorded by the server-side validation
// process and are shown read-only here (the findings PATCH does not accept
// them).
function ValidationPanel({ findings, auditId, onChanged }) {
  const [busy, setBusy] = useState('');
  const cars = findings.filter(f => f.classification === 'car');
  const setMethod = async (f, method) => {
    setBusy(f.id);
    try {
      const res = await fetch(`${API}/api/audit/audits/${auditId}/findings/${f.id}/classification`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(method ? { classification: 'car', validation_method: method } : { classification: 'car' }) });
      const d = await res.json(); if (d.success) onChanged();
    } catch {}
    setBusy('');
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div>
        <div className="text-sm font-semibold text-slate-800">Validation of corrective actions{cars.length ? <span className="text-slate-400 font-normal"> · {cars.length} CAR{cars.length === 1 ? '' : 's'}</span> : null}</div>
        <div className="text-[11px] text-slate-400">Every CAR is validated after remediation: CMS accepts a webinar review, a documentation review, or a validation audit. Only then is the condition truly closed.</div>
      </div>
      {cars.length === 0 && (
        <div className="text-xs text-slate-400">No CARs to validate. Only findings classified as CAR need validation before close out.</div>
      )}
      {cars.map(f => (
        <div key={f.id} className="border border-slate-100 rounded-lg px-3 py-2 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">{f.title}</span>
            {f.validated_at
              ? <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Validated {new Date(f.validated_at).toLocaleDateString()}</span>
              : <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Not validated yet</span>}
            <select value={f.validation_method || ''} disabled={busy === f.id} onChange={e => setMethod(f, e.target.value || null)} className="ml-auto text-xs border border-slate-300 rounded-lg px-2 py-1 text-slate-700">
              <option value="">Validation method…</option>
              {Object.entries(VALIDATION_METHOD_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {f.validation_notes && <div className="text-[11px] text-slate-500">{f.validation_notes}</div>}
        </div>
      ))}
    </div>
  );
}

// Client-side preview of the enrollment-tiered review period (mirrors the
// server's computeReviewPeriod, so the user sees the lookback before saving).
const REVIEW_TIERS = [[50000, 12], [250000, 8], [500000, 4], [Infinity, 2]];
function previewReviewPeriod(enrollment, engDate) {
  const n = Number(enrollment);
  if (!engDate || !Number.isFinite(n) || n <= 0) return null;
  let weeks = null;
  for (const [max, w] of REVIEW_TIERS) { if (n < max) { weeks = w; break; } }
  const end = new Date(`${engDate}T00:00:00Z`);
  if (isNaN(end.getTime())) return null;
  const start = new Date(end); start.setUTCDate(start.getUTCDate() - (weeks * 7 - 1));
  const iso = d => d.toISOString().slice(0, 10);
  return { weeks, start: iso(start), end: iso(end) };
}

// ── Setup form (new audit) ──
function SetupForm({ scope, fdrEntities, builtins, custom, protocols, onCreated, onCancel, onManageProtocols }) {
  const subjectType = scope === 'fdr' ? 'fdr' : 'internal';
  const [subjectRef, setSubjectRef] = useState('');
  const [subjectName, setSubjectName] = useState('');
  const [source, setSource] = useState('protocol');  // protocol | template | generate
  // Focused is the default door: name a regulation or manual, the agent does
  // the rest. Comprehensive opens the full CMS program-audit setup below.
  const [kind, setKind] = useState('focused');       // focused | comprehensive
  const [focusAuthority, setFocusAuthority] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [genMsg, setGenMsg] = useState('');
  const [protocolPick, setProtocolPick] = useState(''); // pinned:key | learned:id
  const [pick, setPick] = useState('');               // builtin:key | custom:id
  const [name, setName] = useState('');
  const [program] = useState('');
  const [authorities, setAuthorities] = useState([]);
  const [standard, setStandard] = useState('');
  const [genCriteria, setGenCriteria] = useState(null);
  const [engagementDate, setEngagementDate] = useState('');
  const [enrollment, setEnrollment] = useState('');
  const [period, setPeriod] = useState({ start: '', end: '' });
  const [scopeNote, setScopeNote] = useState('');
  const [dataIntegrity, setDataIntegrity] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // Departments for the internal-audit picker. An ODAG-style program audit
  // spans several departments, so this is a multi-select; every checked
  // department gets the audit on its "My audits" surface. The free-text
  // subject name stays editable either way.
  const [departments, setDepartments] = useState([]);
  const [subjectDeptIds, setSubjectDeptIds] = useState([]);
  const subjectDeptId = subjectDeptIds[0] || '';
  const toggleDept = (id) => {
    setSubjectDeptIds(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      const names = next.map(x => departments.find(d => d.id === x)?.name).filter(Boolean);
      if (names.length) setSubjectName(names.join(', '));
      return next;
    });
  };
  useEffect(() => {
    fetch(`${API}/api/departments`, { credentials: 'include' })
      .then(r => r.json())
      .then(rows => { if (Array.isArray(rows)) setDepartments(rows); })
      .catch(() => {});
  }, []);

  const pinned = protocols?.pinned || [];
  const learned = (protocols?.learned || []).filter(p => p.status === 'approved');
  const selectedProtocol = protocolPick.startsWith('pinned:')
    ? pinned.find(p => p.key === protocolPick.slice(7))
    : protocolPick.startsWith('learned:') ? learned.find(p => p.id === protocolPick.slice(8)) : null;
  const isEnrollmentTiered = source === 'protocol' && selectedProtocol &&
    (selectedProtocol.enrollment_tiered || selectedProtocol.review_period_type === 'enrollment_tiered');
  const rpPreview = isEnrollmentTiered ? previewReviewPeriod(enrollment, engagementDate) : null;

  const onPickProtocol = (val) => {
    setProtocolPick(val);
    const p = val.startsWith('pinned:') ? pinned.find(x => x.key === val.slice(7)) : learned.find(x => x.id === val.slice(8));
    if (p) setAuthorities(p.authorities || []);
  };
  const onPickTemplate = (val) => {
    setPick(val);
    if (val.startsWith('builtin:')) { const b = builtins.find(t => t.key === val.slice(8)); if (b) setAuthorities(b.authorities || []); }
    else if (val.startsWith('custom:')) { const c = custom.find(t => t.id === val.slice(7)); if (c) setAuthorities(c.authorities || []); }
  };

  const generate = async () => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/api/audit/templates/generate`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, program, authorities, standard_text: standard }) });
      const d = await res.json(); if (!d.success) throw new Error(d.message || 'Generate failed');
      setGenCriteria(d.criteria || []);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  // Focused create: one action. The agent studies the named authority, builds
  // the audit program, creates the audit, and drafts the engagement notice —
  // all reusing the existing endpoints. The user only named the source.
  const createFocused = async () => {
    setBusy(true); setErr('');
    try {
      const subjectNameFinal = subjectType === 'fdr'
        ? (fdrEntities.find(e => e.id === subjectRef)?.name || subjectName)
        : subjectName;
      if (!subjectNameFinal) throw new Error('Name the subject of the audit.');
      const authName = focusAuthority.trim();
      if (!authName) throw new Error('Name the regulation or manual to audit against.');
      const auth = [{ kind: 'other', name: authName }];
      const title = `Focused review — ${authName}`;

      setGenMsg(`Studying ${authName} and building the program…`);
      const gen = await fetch(`${API}/api/audit/templates/generate`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: title, authorities: auth, standard_text: scopeNote || '' }),
      }).then(r => r.json());
      if (!gen.success || !gen.criteria?.length) throw new Error(gen.message || 'The agent could not build a program from that source. Add a line of scope below, or name a more specific citation.');

      setGenMsg('Creating the audit…');
      const body = {
        subject_type: subjectType,
        subject_ref: subjectType === 'fdr' ? (subjectRef || null) : null,
        subject_department_id: subjectType === 'internal' ? (subjectDeptId || null) : null,
        subject_department_ids: subjectType === 'internal' ? subjectDeptIds : [],
        subject_name: subjectNameFinal,
        source_app: scope === 'fdr' ? 'fdr' : 'audit',
        audit_kind: 'focused',
        audit_type: title, template_name: title,
        authorities: auth, criteria: gen.criteria,
        scope_note: scopeNote || null,
        engagement_date: engagementDate || new Date().toISOString().slice(0, 10),
        period_start: period.start || null, period_end: period.end || null,
        data_integrity_applicable: false,
      };
      const res = await fetch(`${API}/api/audit/audits`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(r => r.json());
      if (!res.success) throw new Error(res.message || 'Could not create the audit');

      setGenMsg('Drafting the engagement notice…');
      await fetch(`${API}/api/audit/audits/${res.audit_id}/engagement-letter`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}',
      }).catch(() => {});
      setGenMsg('');
      onCreated(res.audit_id);
    } catch (e) { setErr(e.message); setGenMsg(''); }
    setBusy(false);
  };

  const create = async () => {
    setBusy(true); setErr('');
    try {
      const body = {
        subject_type: subjectType, subject_ref: subjectType === 'fdr' ? (subjectRef || null) : null,
        subject_department_id: subjectType === 'internal' ? (subjectDeptId || null) : null,
        subject_department_ids: subjectType === 'internal' ? subjectDeptIds : [],
        subject_name: subjectType === 'fdr' ? (fdrEntities.find(e => e.id === subjectRef)?.name || subjectName) : subjectName,
        source_app: scope === 'fdr' ? 'fdr' : 'audit',
        authorities, scope_note: scopeNote || null,
        engagement_date: engagementDate || null,
        data_integrity_applicable: dataIntegrity,
      };
      // Period: enrollment-tiered protocols compute the review period server-side;
      // everything else uses the manually entered lookback.
      if (isEnrollmentTiered) { body.enrollment = enrollment ? Number(enrollment) : null; }
      else { body.period_start = period.start || null; body.period_end = period.end || null; }

      if (source === 'protocol') {
        if (!selectedProtocol) throw new Error('Choose a protocol, or teach a new one.');
        if (protocolPick.startsWith('pinned:')) body.protocol_key = protocolPick.slice(7);
        else body.template_id = protocolPick.slice(8);
      } else if (source === 'template') {
        if (pick.startsWith('builtin:')) body.template_key = pick.slice(8);
        else if (pick.startsWith('custom:')) body.template_id = pick.slice(7);
        else throw new Error('Pick an audit type.');
      } else {
        if (!genCriteria || !genCriteria.length) throw new Error('Generate the audit program first.');
        body.criteria = genCriteria; body.template_name = name || 'Custom audit'; body.program = program || null;
      }
      if (!body.subject_name) throw new Error('Name the subject of the audit.');
      const res = await fetch(`${API}/api/audit/audits`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const d = await res.json(); if (!d.success) throw new Error(d.message || 'Could not create audit');
      onCreated(d.audit_id);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="text-sm font-semibold text-slate-800">New audit</div>

      {/* Kind: the first, plain choice — quick focused review vs the full CMS
          program audit. Focused is the default and keeps the form to two fields. */}
      <div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setKind('focused')} className={`text-xs px-3 py-1.5 rounded-lg font-semibold ${kind === 'focused' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`}>Focused review</button>
          <button type="button" onClick={() => setKind('comprehensive')} className={`text-xs px-3 py-1.5 rounded-lg font-semibold ${kind === 'comprehensive' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`}>Comprehensive / CMS program audit</button>
        </div>
        <div className="text-[11px] text-slate-400 mt-1">
          {kind === 'focused'
            ? 'A targeted review of one regulation or manual. Name the source; the agent builds the program and drafts the engagement notice for you to review.'
            : 'The full CMS routine program-audit workspace: protocol, universe submission, sampling, conferences, and the preliminary-to-final report cycle.'}
        </div>
      </div>

      {/* Subject */}
      <div>
        <div className="text-[11px] font-semibold text-slate-500 mb-1">Who/what is being audited</div>
        {/* The subject follows the app: a vendor in the FDR app, a department here. No toggle. */}
        {subjectType === 'fdr'
          ? <select value={subjectRef} onChange={e => setSubjectRef(e.target.value)} className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 text-slate-700"><option value="">Choose an FDR…</option>{fdrEntities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}</select>
          : (
            <div className="space-y-2">
              {departments.length > 0 && (
                <div>
                  <div className="text-[11px] text-slate-400 mb-1">
                    Pick the audited department(s) — an ODAG or program audit usually spans several. Each checked team answers requests from its own view.
                    {subjectDeptIds.length > 0 && <span className="ml-1 font-semibold text-orange-600">{subjectDeptIds.length} selected</span>}
                  </div>
                  <div className="max-h-44 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" aria-label="Audited departments">
                    {departments.map(d => (
                      <label key={d.id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50">
                        <input type="checkbox" checked={subjectDeptIds.includes(d.id)} onChange={() => toggleDept(d.id)} className="rounded border-slate-300" />
                        <span className="text-[12px] text-slate-700">{d.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <input value={subjectName} onChange={e => setSubjectName(e.target.value)} placeholder="Department or process (e.g., Claims Operations, Appeals & Grievances)" className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2" />
            </div>
          )}
      </div>

      {/* Focused: name the source, that is the whole form. */}
      {kind === 'focused' && (
        <div className="space-y-2">
          <div className="text-[11px] font-semibold text-slate-500">What are you auditing against?</div>
          <input value={focusAuthority} onChange={e => setFocusAuthority(e.target.value)}
            placeholder="A regulation or a manual — e.g., 42 CFR §422.568, or CMS ODAG protocol, or NCQA UM standards"
            className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2" />
          <div className="text-[11px] text-slate-400">Name the source. The agent reads it, builds the tested conditions, and drafts the engagement notice. Nothing goes out until you review it.</div>
          <textarea value={scopeNote} onChange={e => setScopeNote(e.target.value)} rows={2}
            placeholder="Optional: one line on what prompted this or where to focus (e.g., Q2 Part C denials look slow)."
            className="w-full text-xs border border-slate-300 rounded-lg px-3 py-2" />
          <button type="button" onClick={() => setAdvanced(a => !a)} className="text-[11px] font-semibold text-slate-400 hover:text-slate-600">{advanced ? '− Hide dates' : '+ Set dates (optional)'}</button>
          {advanced && (
            <div className="space-y-2 border-l-2 border-slate-100 pl-3">
              <label className="block text-xs text-slate-500">Engagement date <span className="text-slate-400">(defaults to today)</span>
                <input type="date" value={engagementDate} onChange={e => setEngagementDate(e.target.value)} className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5" />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs text-slate-500">Review period start <span className="text-slate-400">(lookback)</span><input type="date" value={period.start} onChange={e => setPeriod(p => ({ ...p, start: e.target.value }))} className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5" /></label>
                <label className="text-xs text-slate-500">Review period end<input type="date" value={period.end} onChange={e => setPeriod(p => ({ ...p, end: e.target.value }))} className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5" /></label>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Comprehensive: the full CMS program-audit setup. */}
      {kind === 'comprehensive' && <>
      {/* Audit type */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[11px] font-semibold text-slate-500">Audit type</div>
          {onManageProtocols && <button onClick={onManageProtocols} className="text-[11px] font-semibold text-orange-600 hover:underline">+ Teach a new protocol from its source</button>}
        </div>
        <div className="flex gap-2 mb-2">
          <button onClick={() => setSource('protocol')} className={`text-xs px-3 py-1.5 rounded-lg font-semibold ${source === 'protocol' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`}>Use a protocol</button>
          <button onClick={() => setSource('template')} className={`text-xs px-3 py-1.5 rounded-lg font-semibold ${source === 'template' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`}>Pick a template</button>
          <button onClick={() => setSource('generate')} className={`text-xs px-3 py-1.5 rounded-lg font-semibold ${source === 'generate' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`}>Quick generate</button>
        </div>
        {source === 'protocol' && (
          <div className="space-y-2">
            <select value={protocolPick} onChange={e => onPickProtocol(e.target.value)} className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 text-slate-700">
              <option value="">Choose a protocol…</option>
              <optgroup label="CMS protocols (ready to use)">{pinned.map(p => <option key={p.key} value={`pinned:${p.key}`}>{p.name}</option>)}</optgroup>
              {learned.length > 0 && <optgroup label="Your learned protocols">{learned.map(p => <option key={p.id} value={`learned:${p.id}`}>{p.name}</option>)}</optgroup>}
            </select>
            {selectedProtocol && (
              <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                {selectedProtocol.criteria_count} tested condition{selectedProtocol.criteria_count === 1 ? '' : 's'}
                {selectedProtocol.universe_count ? ` · ${selectedProtocol.universe_count} universes` : ''}
                {selectedProtocol.grounding ? ` · ${selectedProtocol.grounding.coverage}% source-grounded` : ''}
                {isEnrollmentTiered && <span> · review period computed from enrollment</span>}
              </div>
            )}
            {!pinned.length && !learned.length && <div className="text-[11px] text-slate-400">No protocols yet. Use the link above to upload a protocol document — it is learned once and reused for every audit of that type.</div>}
          </div>
        )}
        {source === 'template' && (
          <select value={pick} onChange={e => onPickTemplate(e.target.value)} className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 text-slate-700">
            <option value="">Choose an audit type…</option>
            <optgroup label="Built-in">{builtins.map(t => <option key={t.key} value={`builtin:${t.key}`}>{t.name}</option>)}</optgroup>
            {custom.length > 0 && <optgroup label="Your templates">{custom.map(t => <option key={t.id} value={`custom:${t.id}`}>{t.name}</option>)}</optgroup>}
          </select>
        )}
        {source === 'generate' && (
          <div className="space-y-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">AI draft — not source-grounded</div>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Audit type name (e.g., Utilization Management Audit)" className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2" />
            <textarea value={standard} onChange={e => setStandard(e.target.value)} rows={4} placeholder="Optional: paste the governing standard. For a full, reusable, source-grounded protocol, prefer 'Teach a new protocol' above instead of a one-off generate." className="w-full text-xs border border-slate-300 rounded-lg px-3 py-2" />
            {!genCriteria ? (
              <button onClick={generate} disabled={busy || !name} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">{busy ? 'Building the audit program…' : 'Generate audit program'}</button>
            ) : (
              <div className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">{genCriteria.length} criteria generated <span className="text-amber-700 font-semibold">(AI draft — not source-grounded)</span>. They'll be saved with this audit. <button onClick={() => setGenCriteria(null)} className="underline">Re-generate</button></div>
            )}
          </div>
        )}
      </div>

      <AuthoritiesEditor value={authorities} onChange={setAuthorities} />

      {/* Timeline + period (kept strictly separate) */}
      <div className="space-y-2">
        <label className="block text-xs text-slate-500">Engagement date <span className="text-slate-400">(when the audit/engagement letter is issued)</span>
          <input type="date" value={engagementDate} onChange={e => setEngagementDate(e.target.value)} className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5" />
        </label>
        {isEnrollmentTiered ? (
          <div>
            <label className="block text-xs text-slate-500">Enrollment (MA/MAPD or PDP) <span className="text-slate-400">— sets the review-period length</span>
              <input type="number" value={enrollment} onChange={e => setEnrollment(e.target.value)} placeholder="e.g., 42000" className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5" />
            </label>
            <div className="text-[11px] text-slate-500 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mt-1">
              {rpPreview
                ? <>Review period (lookback): <b>{rpPreview.start}</b> to <b>{rpPreview.end}</b> ({rpPreview.weeks}-week window ending on the engagement date). This is distinct from the audit timeline.</>
                : <>Enter the engagement date and enrollment to compute the review period. The lookback always ends on the engagement date, it is never in the future.</>}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-slate-500">Review period start <span className="text-slate-400">(lookback)</span><input type="date" value={period.start} onChange={e => setPeriod(p => ({ ...p, start: e.target.value }))} className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5" /></label>
            <label className="text-xs text-slate-500">Review period end<input type="date" value={period.end} onChange={e => setPeriod(p => ({ ...p, end: e.target.value }))} className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5" /></label>
          </div>
        )}
      </div>
      <textarea value={scopeNote} onChange={e => setScopeNote(e.target.value)} rows={2} placeholder="Scope notes (optional)" className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2" />
      <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={dataIntegrity} onChange={e => setDataIntegrity(e.target.checked)} /> This audit involves a data universe needing integrity validation (e.g., ODAG/CDAG via the Scrubber)</label>
      </>}

      {err && <div className="text-xs text-red-600">{err}</div>}
      <div className="flex gap-2 items-center">
        <button onClick={kind === 'focused' ? createFocused : create} disabled={busy} className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">{busy ? (genMsg || 'Creating…') : (kind === 'focused' ? 'Create focused audit' : 'Create audit')}</button>
        <button onClick={onCancel} className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-slate-300 text-slate-600">Cancel</button>
        {busy && genMsg && <span className="text-[11px] text-slate-500">{genMsg}</span>}
      </div>
    </div>
  );
}

// ── Audit program preview (the criteria, before fieldwork) ──
function ProgramPanel({ criteria }) {
  const [open, setOpen] = useState(false);
  if (!criteria.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between text-left">
        <div className="text-sm font-semibold text-slate-800">Audit program <span className="text-slate-400 font-normal">· {criteria.length} tested condition{criteria.length === 1 ? '' : 's'}</span></div>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {!open && <div className="text-[11px] text-slate-400 mt-1">The conditions this audit will test the evidence against. Review them before running fieldwork.</div>}
      {open && (
        <div className="mt-3 space-y-2">
          {criteria.map((c, i) => (
            <div key={c.key || i} className="border border-slate-100 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${SEV_CFG[c.severity] || SEV_CFG.low}`}>{c.severity || 'medium'}</span>
                <span className="text-sm font-semibold text-slate-800">{c.title}</span>
              </div>
              {c.citation && <div className="text-[11px] font-mono text-slate-400 mt-0.5">{c.citation}</div>}
              {c.test && <div className="text-[12px] text-slate-500 mt-1"><span className="font-semibold text-slate-400">Tested by: </span>{c.test}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Data request (universes + record layouts), rendered from the protocol spec ──
// Protocol table → the Scrubber's universe key, exactly as its validators
// name them. Only tables with a shipped validator appear in the picker
// (ODAG Table 6/AIP is suspended and has none).
const UNIVERSE_TYPE_KEYS = {
  ODAG: { 'Table 1': 'odag_table1', 'Table 2': 'odag_table2_reconsiderations', 'Table 3': 'odag_table3_pymt_c', 'Table 4': 'odag_table4_eff_c', 'Table 5': 'odag_table5_grv_c' },
  CDAG: { 'Table 1': 'cdag_table1_cd', 'Table 2': 'cdag_table2_cder', 'Table 3': 'cdag_table3_pymt_d', 'Table 4': 'cdag_table4_rd', 'Table 5': 'cdag_table5_eff_d', 'Table 6': 'cdag_table6_grv_d', 'Table 7': 'cdag_table7_ar' },
};
function universeTypeOptions(audit) {
  // The protocol identity lives in the template key/name (audit_type is a
  // generic classification like "Auditing").
  const idText = `${audit?.template_key || ''} ${audit?.template_name || ''}`;
  const proto = /odag/i.test(idText) ? 'ODAG' : /cdag/i.test(idText) ? 'CDAG' : null;
  const keys = UNIVERSE_TYPE_KEYS[proto] || {};
  const universes = audit?.data_request?.universes || [];
  return universes
    .filter(u => keys[u.id])
    .map(u => ({ value: keys[u.id], label: `${u.id} — ${String(u.name || '').slice(0, 40)}` }));
}

function DataRequestPanel({ dataRequest, schedule }) {
  const [openU, setOpenU] = useState(null);
  const dr = dataRequest;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="text-sm font-semibold text-slate-800">Data and evidence request</div>
      <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
        {dr.review_period?.start && <span>Review period: <b>{dr.review_period.start}</b> to <b>{dr.review_period.end}</b>{dr.review_period.weeks ? ` (${dr.review_period.weeks} wks)` : ''}</span>}
        {dr.submission?.due_date && <span>Submission due: <b>{dr.submission.due_date}</b></span>}
      </div>
      <div className="space-y-2">
        {(dr.universes || []).map((u, i) => (
          <div key={u.id || i} className="border border-slate-100 rounded-lg">
            <button onClick={() => setOpenU(openU === i ? null : i)} className="w-full flex items-start justify-between gap-2 px-3 py-2 text-left">
              <div>
                <div className="text-sm font-semibold text-slate-800">{u.id ? `${u.id} — ` : ''}{u.name}{u.suspended && <span className="ml-2 text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">suspended</span>}</div>
                {u.contains && <div className="text-[11px] text-slate-500 mt-0.5">{u.contains}</div>}
              </div>
              {Array.isArray(u.record_layout) && u.record_layout.length > 0 && <span className="text-[11px] text-slate-400 flex-shrink-0">{u.record_layout.length} fields</span>}
            </button>
            {openU === i && Array.isArray(u.record_layout) && u.record_layout.length > 0 && (
              <div className="px-3 pb-3 overflow-x-auto">
                {u.key_date_field && <div className="text-[11px] text-slate-500 mb-1"><b>Key date:</b> {u.key_date_field}</div>}
                {u.exclusions && <div className="text-[11px] text-slate-500 mb-1"><b>Excludes:</b> {u.exclusions}</div>}
                <table className="w-full text-[11px] border-collapse">
                  <thead><tr className="text-slate-400 text-left"><th className="pr-2 font-semibold">Col</th><th className="pr-2 font-semibold">Field</th><th className="pr-2 font-semibold">Len</th><th className="font-semibold">Notes</th></tr></thead>
                  <tbody>
                    {u.record_layout.map((c, j) => (
                      <tr key={j} className="border-t border-slate-100 align-top">
                        <td className="pr-2 py-0.5 font-mono text-slate-500">{c.col || ''}</td>
                        <td className="pr-2 py-0.5 text-slate-700">{c.field}</td>
                        <td className="pr-2 py-0.5 text-slate-400">{c.length || ''}</td>
                        <td className="py-0.5 text-slate-400">{c.notes || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
      {Array.isArray(dr.sample_plan) && dr.sample_plan.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Sample plan</div>
          {dr.sample_plan.map((s, i) => <div key={i} className="text-[12px] text-slate-600">• {s.name}: {s.method}{s.n ? ` (${s.n} cases)` : ''}</div>)}
        </div>
      )}
      {Array.isArray(dr.submission?.rules) && dr.submission.rules.length > 0 && (
        <details className="text-[11px] text-slate-500">
          <summary className="cursor-pointer font-semibold text-slate-400">Submission format and rules</summary>
          <ul className="list-disc list-inside mt-1 space-y-0.5">{dr.submission.rules.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </details>
      )}
    </div>
  );
}

// ── Send-the-notice dialog: email it, or record a manual send ──
export function SendLetterDialog({ auditId, audit = null, onClose, onSent }) {
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showMark, setShowMark] = useState(false);
  const [sentTo, setSentTo] = useState('');
  // Recipients for an internal audit: the users of EVERY audited department
  // (an ODAG audit spans several), grouped by department, plus the org's
  // leadership accounts. Empty for FDR audits (free text serves).
  const [deptGroups, setDeptGroups] = useState([]); // [{ name, users }]
  const [leaders, setLeaders] = useState([]);
  const [picked, setPicked] = useState(() => new Set());
  // The server keeps the first department in subject_ref and the full set in
  // subject_department_ids.
  const auditDeptIds = useMemo(() => {
    if (audit?.subject_type !== 'internal') return [];
    const multi = Array.isArray(audit?.subject_department_ids) ? audit.subject_department_ids : [];
    const ids = multi.length ? multi : (audit?.subject_ref ? [audit.subject_ref] : []);
    return [...new Set(ids.filter(Boolean).map(String))];
  }, [audit?.subject_type, audit?.subject_department_ids, audit?.subject_ref]);
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        if (auditDeptIds.length) {
          const depts = await fetch(`${API}/api/departments`, { credentials: 'include' })
            .then(r => r.json()).catch(() => []);
          const nameOf = (id) => (Array.isArray(depts) ? depts : []).find(d => String(d.id) === id)?.name || 'Department under audit';
          const groups = await Promise.all(auditDeptIds.map(async (id) => {
            const d = await fetch(`${API}/api/compliance-issues-log/users-by-department/${id}`, { credentials: 'include' })
              .then(r => r.json()).catch(() => ({}));
            return { name: nameOf(id), users: d.users || [] };
          }));
          if (!dead) {
            const kept = groups.filter(g => g.users.length);
            setDeptGroups(kept);
            // The audited teams ARE the auditee: their users start checked.
            // Leadership stays opt-in. Unticking anyone remains one click.
            setPicked(prev => prev.size ? prev : new Set(
              kept.flatMap(g => g.users.map(u => u.email).filter(Boolean))
            ));
          }
        }
        const lead = await fetch(`${API}/api/audit/leadership-users`, { credentials: 'include' })
          .then(r => r.json()).catch(() => ({}));
        if (!dead) setLeaders(lead.users || []);
      } catch { /* free text always remains */ }
    })();
    return () => { dead = true; };
  }, [auditDeptIds]);
  // Leadership shows only people not already listed under a department.
  const deptEmails = new Set(deptGroups.flatMap(g => g.users.map(u => (u.email || '').toLowerCase())));
  const leaderList = leaders.filter(u => u.email && !deptEmails.has(u.email.toLowerCase()));
  const anyUsers = deptGroups.length > 0 || leaderList.length > 0;
  const togglePick = (email) => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(email)) next.delete(email); else next.add(email);
    return next;
  });

  const send = async () => {
    setBusy(true); setErr('');
    try {
      const recipients = [...picked];
      if (to.trim()) recipients.push(to.trim());
      if (!recipients.length) throw new Error('Pick at least one recipient or enter an email.');
      // One audit, one notice, one email: everyone rides the same To line,
      // so every recipient sees who was noticed.
      const res = await fetch(`${API}/api/audit/audits/${auditId}/engagement-letter/send`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(note.trim() ? { to: recipients, note: note.trim() } : { to: recipients }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || 'The email did not go out.');
      await onSent(); onClose();
    } catch (e) { setErr(e.message || 'The email did not go out.'); setShowMark(true); if (!sentTo) setSentTo(to.trim()); }
    setBusy(false);
  };

  const markSent = async () => {
    setBusy(true); setErr('');
    try {
      const d = await fetch(`${API}/api/audit/audits/${auditId}/engagement-letter/mark-sent`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sentTo.trim() ? { sent_to: sentTo.trim() } : {}),
      }).then(r => r.json());
      if (!d.success) throw new Error(d.message || 'Could not record the send.');
      await onSent(); onClose();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" role="dialog" aria-label="Send engagement notice">
      <div className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-md p-4 space-y-3">
        <div className="text-sm font-semibold text-slate-800">Send the engagement notice</div>
        <p className="text-[12px] text-slate-500">Emails the notice to the auditee with the Word version attached, and records the send on the audit trail.</p>
        {anyUsers && (
          <div className="text-xs text-slate-500">
            <div className="mb-1">Pick the recipients — the audited teams and leadership</div>
            <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-200">
              {deptGroups.map(g => (
                <div key={g.name}>
                  <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 bg-slate-50 border-b border-slate-100 sticky top-0">{g.name}</div>
                  {g.users.map(u => (
                    <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50 border-b border-slate-50">
                      <input type="checkbox" checked={picked.has(u.email)} onChange={() => togglePick(u.email)} className="rounded border-slate-300" />
                      <span className="text-[12px] text-slate-700">{u.name || u.email}</span>
                      <span className="text-[11px] text-slate-400 ml-auto">{u.email}</span>
                    </label>
                  ))}
                </div>
              ))}
              {leaderList.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 bg-slate-50 border-b border-slate-100 sticky top-0">Leadership</div>
                  {leaderList.map(u => (
                    <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50 border-b border-slate-50">
                      <input type="checkbox" checked={picked.has(u.email)} onChange={() => togglePick(u.email)} className="rounded border-slate-300" />
                      <span className="text-[12px] text-slate-700">{u.name || u.email}</span>
                      <span className="text-[11px] text-slate-400 ml-auto">{u.email}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <label className="block text-xs text-slate-500">{anyUsers ? 'Additional email (optional)' : 'Auditee contact email'}
          <input value={to} onChange={e => setTo(e.target.value)} placeholder="name@example.com" className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-3 py-2" />
        </label>
        <label className="block text-xs text-slate-500">Cover note (optional)
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="A short message above the notice…" className="mt-1 w-full text-xs border border-slate-300 rounded-lg px-3 py-2" />
        </label>
        {err && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</div>}
        {showMark && (
          <div className="border border-amber-200 bg-amber-50 rounded-lg px-3 py-2 space-y-2">
            <div className="text-[12px] text-amber-800">Sending it yourself instead? Record it here so the audit can move on.</div>
            <input value={sentTo} onChange={e => setSentTo(e.target.value)} placeholder="Sent to (optional)" className="w-full text-xs border border-amber-300 rounded-lg px-2 py-1.5" />
            <button onClick={markSent} disabled={busy} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white shadow disabled:opacity-50">{busy ? 'Recording…' : 'Mark as sent'}</button>
          </div>
        )}
        <div className="flex items-center gap-2 justify-end">
          {!showMark && <button onClick={() => setShowMark(true)} className="mr-auto text-[11px] text-slate-400 hover:text-slate-600">Sent it outside the platform?</button>}
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-600">Cancel</button>
          <button onClick={send} disabled={busy || (!to.trim() && picked.size === 0)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">{busy ? 'Sending…' : (picked.size > 1 ? `Send to ${picked.size + (to.trim() ? 1 : 0)}` : 'Send')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Kickoff: the engagement notice — editable, rebuildable, and sendable ──
function EngagementNoticePanel({ audit, auditId, onChanged }) {
  const [draft, setDraft] = useState(audit.engagement_letter || '');
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [sendOpen, setSendOpen] = useState(false);

  // Follow server-side changes (rebuilds, other sessions) unless the user has
  // unsaved edits — never clobber work in progress on a background refresh.
  useEffect(() => { if (!dirty) setDraft(audit.engagement_letter || ''); }, [audit.engagement_letter, dirty]);

  const act = async (fn, key) => { setBusy(key); setErr(''); try { await fn(); } catch (e) { setErr(e.message); } setBusy(''); };
  const rebuild = () => {
    if (draft && !window.confirm('Rebuilding overwrites your edits to the notice. Continue?')) return;
    act(async () => {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/engagement-letter`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' }).then(r => r.json());
      if (!r.success) throw new Error(r.message);
      setDirty(false); setSaved(false); setDraft(r.letter);
      await onChanged();
    }, 'gen');
  };
  const save = () => act(async () => {
    const r = await fetch(`${API}/api/audit/audits/${auditId}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ engagement_letter: draft }) }).then(r => r.json());
    if (!r.success) throw new Error(r.message || 'Could not save the notice');
    setDirty(false); setSaved(true);
    await onChanged();
  }, 'save');

  const sentAt = audit.engagement_letter_sent_at;
  const [mode, setMode] = useState('preview'); // preview | edit
  const [logoOk, setLogoOk] = useState(true);

  // Render the letter as a letter: ALL CAPS lines are section headings.
  const letterBlocks = (draft || '').split('\n').map((line, i) => {
    const trimmed = line.trim();
    const isHeading = /^[A-Z][A-Z\s&/]{3,}$/.test(trimmed) && trimmed.length < 60;
    return { key: i, line, isHeading };
  });

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-800">Engagement notice</div>
          <div className="text-[11px] text-slate-400">The formal letter that opens the audit, in the CMS program audit format: authority, scope, review period, phases, universe request, and disclosed issues.</div>
        </div>
        <div className="flex gap-3 items-center">
          {draft && <a href={`${API}/api/audit/audits/${auditId}/engagement-letter.docx`} className="text-xs font-semibold text-slate-600 hover:text-slate-800">↓ Word</a>}
          <button onClick={rebuild} disabled={busy === 'gen'} className="text-xs font-semibold text-orange-600 disabled:opacity-50">{busy === 'gen' ? 'Building…' : (draft ? 'Rebuild' : 'Generate engagement notice')}</button>
          {draft && <button onClick={() => setSendOpen(true)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow">Send to auditee</button>}
        </div>
      </div>
      {sentAt && (
        <div className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5">
          <span aria-hidden="true">✓</span>
          Sent to {audit.engagement_letter_sent_to || 'the auditee'} on {new Date(sentAt).toLocaleDateString()} {audit.engagement_letter_sent_via === 'email' ? 'via email' : 'manually'}
        </div>
      )}
      {draft ? (
        <>
          <div className="inline-flex p-0.5 rounded-lg bg-slate-100 gap-0.5">
            {['preview', 'edit'].map(m => (
              <button key={m} type="button" onClick={() => setMode(m)}
                className={`px-3 py-1 text-[11px] font-semibold rounded-md transition-all ${mode === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                {m === 'preview' ? 'Letter' : 'Edit text'}
              </button>
            ))}
          </div>

          {mode === 'preview' ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 sm:p-8 max-h-[560px] overflow-y-auto">
              <div className="mx-auto max-w-[46rem] bg-white rounded-lg shadow-sm border border-slate-200/70 px-8 py-9">
                {/* Letterhead: the org logo, same source Deck Studio uses. */}
                {logoOk && (
                  <img src={`${API}/api/deck-studio/branding/logo`} alt="" onError={() => setLogoOk(false)}
                    className="h-11 max-w-[190px] object-contain mb-6" />
                )}
                <div className="text-[13px] leading-relaxed text-slate-800">
                  {letterBlocks.map(b => b.isHeading
                    ? <div key={b.key} className="font-bold tracking-wide text-slate-900 mt-5 mb-1.5 text-[12px]">{b.line}</div>
                    : <div key={b.key} className={`whitespace-pre-wrap ${b.line.trim() ? '' : 'h-3'}`}>{b.line}</div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              <textarea value={draft} onChange={e => { setDraft(e.target.value); setDirty(true); setSaved(false); }} rows={16} className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 text-slate-700 font-mono leading-relaxed" />
              <div className="flex items-center gap-2">
                <button onClick={save} disabled={busy === 'save' || !dirty} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-800 text-white shadow disabled:opacity-50">{busy === 'save' ? 'Saving…' : 'Save edits'}</button>
                {saved && !dirty && <span className="text-[11px] text-emerald-600">✓ Saved</span>}
                {dirty && <span className="text-[11px] text-amber-600">Unsaved edits</span>}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="text-xs text-slate-400">Generate the notice to see it here as a letter, on your letterhead, in the CMS engagement format.</div>
      )}
      {err && <div className="text-xs text-red-600">{err}</div>}
      {sendOpen && <SendLetterDialog auditId={auditId} audit={audit} onClose={() => setSendOpen(false)} onSent={onChanged} />}
    </div>
  );
}

// ── Audit detail (full lifecycle) ──
export function AuditDetail({ auditId, onBack }) {
  const [audit, setAudit] = useState(null);
  const [results, setResults] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [findings, setFindings] = useState([]);
  const [workflow, setWorkflow] = useState(null);
  const [counts, setCounts] = useState(null);
  const [loadCount, setLoadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  // Correspondence composer: null = closed, {} = blank, or a prefill
  // ({kind, to, subject, body, attach}) from the RCA/impact or report panels.
  const [compose, setCompose] = useState(null);
  const [corrKey, setCorrKey] = useState(0);
  // The fieldwork agent endpoint may not exist on this server yet; the first
  // 404 hides the button for the session.
  const [agentGone, setAgentGone] = useState(false);
  // The left rail drives which phase (or the binder) shows in the center.
  // Panels for every phase stay MOUNTED — the rail only toggles visibility —
  // so switching phases never remounts a panel or loses its state.
  const [activeView, setActiveView] = useState('p1');
  const fileRef = useRef(null);
  const uniRef = useRef(null);

  // One loader for everything: the audit row AND the workflow (legal moves,
  // blockers, counts). Every mutating action calls this, so the lifecycle bar
  // is always honest about what is allowed right now.
  const load = useCallback(async () => {
    try {
      const [res, wres] = await Promise.all([
        fetch(`${API}/api/audit/audits/${auditId}`, { credentials: 'include' }),
        fetch(`${API}/api/audit/audits/${auditId}/workflow`, { credentials: 'include' }),
      ]);
      const d = await res.json();
      if (d.success) { setAudit(d.audit); setResults(d.results || []); setDocuments(d.documents || []); setFindings(d.findings || []); }
      try { const w = await wres.json(); if (w.success) { setWorkflow(w.workflow); setCounts(w.counts); } } catch {}
      setLoadCount(n => n + 1);
    } catch {}
    setLoading(false);
  }, [auditId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!audit || !RUNNING.has(audit.status)) return; const t = setInterval(load, 4000); return () => clearInterval(t); }, [audit, load]);

  // Open the current phase in the rail on load, and follow the audit when it
  // advances into a new phase — mirroring the old accordion's auto-open. Keyed
  // on the stage itself so it fires once per real advance, never fighting a
  // user who has clicked to another phase within the same stage.
  const lastStage = useRef(null);
  useEffect(() => {
    if (!audit) return;
    if (lastStage.current === audit.stage) return;
    lastStage.current = audit.stage;
    setActiveView(`p${PHASE_OF_STAGE[audit.stage] || 1}`);
  }, [audit]);

  const act = async (fn, key) => { setBusy(key); setError(''); try { await fn(); } catch (e) { setError(e.message); } setBusy(''); };
  const saveDI = (status) => act(async () => { await fetch(`${API}/api/audit/audits/${auditId}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ data_integrity_status: status, stage: 'fieldwork' }) }); await load(); }, 'di');
  const toggleDataCheck = (on) => act(async () => {
    const r = await fetch(`${API}/api/audit/audits/${auditId}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ data_integrity_applicable: on }) }).then(r => r.json());
    if (!r.success) throw new Error(r.message || 'Could not update the audit');
    await load();
  }, 'ditoggle');
  // The table picker answers "how does the app know the table type?" — it
  // asks, exactly like the Scrubber does. Auto-detect stays available for
  // files whose name/headers make the type obvious.
  const [uniType, setUniType] = useState('');
  const validateUni = (file) => act(async () => {
    const fd = new FormData(); fd.append('file', file);
    if (uniType) fd.append('universe_type', uniType);
    const res = await fetch(`${API}/api/audit/audits/${auditId}/validate-universe`, { method: 'POST', credentials: 'include', body: fd });
    const d = await res.json();
    if (!d.success) throw new Error(d.message || 'The check did not run');
    await load();
  }, 'uni');
  const createCap = () => act(async () => {
    const res = await fetch(`${API}/api/audit/audits/${auditId}/create-cap`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    const d = await res.json(); if (!d.success) throw new Error(d.message);
    await load();
  }, 'cap');
  const upload = (file) => act(async () => { const fd = new FormData(); fd.append('file', file); const res = await fetch(`${API}/api/audit/audits/${auditId}/documents`, { method: 'POST', credentials: 'include', body: fd }); const d = await res.json(); if (!d.success) throw new Error(d.message); await load(); }, 'up');
  const run = () => act(async () => { const res = await fetch(`${API}/api/audit/audits/${auditId}/run`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' }); const d = await res.json(); if (!d.success) throw new Error(d.message); await load(); }, 'run');
  const runAgent = () => act(async () => {
    const res = await fetch(`${API}/api/audit/audits/${auditId}/run-agent`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    if (res.status === 404) { setAgentGone(true); throw new Error('The fieldwork agent is not available on this server yet.'); }
    const d = await res.json(); if (!d.success) throw new Error(d.message); await load();
  }, 'agent');
  const promote = () => act(async () => { const res = await fetch(`${API}/api/audit/audits/${auditId}/findings`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' }); const d = await res.json(); if (!d.success) throw new Error(d.message); await load(); }, 'promote');
  // Closing destroys PHI (by design): first call returns the real
  // destruction preview for the modal; the confirmed call closes, sweeps,
  // and returns the certificate.
  const [closePreview, setClosePreview] = useState(null);
  const close = () => act(async () => {
    const res = await fetch(`${API}/api/audit/audits/${auditId}/close`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ confirm_erase: true }) });
    const d = await res.json(); if (!d.success) throw new Error(d.message || 'Close failed');
    setClosePreview(null);
    await load();
  }, 'close');
  // Wrapping up is deliberate: if deficient results were never raised as
  // findings, say so before the record is finalized; then show exactly what
  // closing will destroy.
  const confirmClose = () => act(async () => {
    const n = counts?.unraisedDeficient || 0;
    if (n > 0 && !window.confirm(`${n} deficient result${n === 1 ? ' has' : 's have'} not been raised as findings. Wrap up the audit anyway?`)) return;
    const res = await fetch(`${API}/api/audit/audits/${auditId}/close`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    const d = await res.json(); if (!d.success) throw new Error(d.message || 'Could not load the close preview');
    setClosePreview(d.preview || {});
  }, 'close-preview');

  if (loading) return <div className="text-sm text-slate-400 py-6">Loading audit…</div>;
  if (!audit) return <div className="text-sm text-slate-400 py-6">Audit not found. <button onClick={onBack} className="text-orange-600">Back</button></div>;

  const isRunning = RUNNING.has(audit.status);
  // Focused reviews run the lean flow: engagement notice, evidence, grounded
  // review, findings, report, close. The CMS program-audit apparatus (universe
  // submission, sampling, PAIS, conferences, three-attempt rule, report cycle)
  // is hidden — it is noise for a targeted review.
  const isFocused = audit.audit_kind === 'focused';
  const posture = audit.compliance_posture ? (POSTURE_CFG[audit.compliance_posture] || POSTURE_CFG.not_assessable) : null;
  const actions = audit.token_usage?.top_actions || [];
  const promotable = results.filter(r => ['deficient', 'missing'].includes(r.status) && !r.finding_id).length;
  // The human path: the report, findings, and wrap-up are available once the
  // audit reaches fieldwork — the grounded review is optional, not a gate.
  const inReporting = ['fieldwork', 'reporting', 'closed'].includes(audit.stage) || audit.status === 'completed';
  const currentPhase = PHASE_OF_STAGE[audit.stage] || 1;

  // Live status lines for the rail — the same truths the panels show, in a
  // word or two so the rail reads as a status report before you click.
  const diStatusLabel = { validated: 'universe accepted', issues_found: 'universe has issues', failed: 'universe sent back', pending: 'awaiting universe' }[audit.data_integrity_status || 'pending'];
  const railStatuses = {
    p1: audit.engagement_date
      ? `Notice sent ${fmtHeaderDate(audit.engagement_date)}${audit.data_integrity_applicable ? ` · ${diStatusLabel}` : ''}`
      : (audit.data_integrity_applicable ? `Set up · ${diStatusLabel}` : 'Set up & notify'),
    p2: audit.status === 'completed'
      ? `${audit.criteria_met ?? 0} met · ${audit.criteria_deficient ?? 0} deficient`
      : (isRunning ? 'Review running…' : (documents.length ? `${documents.length} evidence file${documents.length === 1 ? '' : 's'}` : 'Awaiting evidence')),
    p3: findings.length ? `${findings.length} finding${findings.length === 1 ? '' : 's'}` : 'No report yet',
    p4: audit.stage === 'closed' ? `Closed${audit.closed_at ? ` ${new Date(audit.closed_at).toLocaleDateString()}` : ''}` : 'Open',
  };
  const binderCount = binderFolders(audit).length;

  // Prefilled correspondence: RCA / impact requests and report versions open
  // the composer with the right kind, subject, body, and attachment.
  const onCorrespondenceSent = async () => { setCorrKey(k => k + 1); await load(); };
  const emailAnalysis = (r) => {
    const isRca = r.kind === 'root_cause';
    const days = isRca ? '2 business days' : '10 business days';
    setCompose({
      kind: isRca ? 'rca_request' : 'impact_request',
      subject: `${isRca ? 'Root cause analysis' : 'Impact analysis'} request: ${audit.subject_name || 'compliance audit'}`,
      body: `As part of the ${audit.template_name || 'compliance audit'}, please provide a ${isRca ? 'root cause analysis' : 'impact analysis'} for the issue identified during testing. Per the CMS program audit process it is due within ${days}${r.due_date ? `, by ${String(r.due_date).slice(0, 10)}` : ''}. Reply to this message with the analysis, or send it through your usual channel and we will record it.`,
    });
  };
  const emailReport = (report) => {
    const kindMap = { preliminary: 'prelim_report', draft: 'draft_report', final: 'final_report' };
    const label = (REPORT_KIND_LABELS[report.kind] || report.kind).toLowerCase();
    setCompose({
      kind: kindMap[report.kind] || 'custom',
      attach: `report:${report.id}`,
      subject: `${REPORT_KIND_LABELS[report.kind] || 'Audit'} audit report: ${audit.subject_name || 'compliance audit'}`,
      body: `Attached is the ${label} audit report for ${audit.subject_name || 'the audit'}.${report.kind === 'draft' ? ' Please review it and return your written comments within 10 business days so they can be considered before the final report.' : ''}`,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700">← Audits</button>
      </div>
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-base font-bold text-slate-800">{audit.template_name}</div>
          {isFocused && <span className="text-[10px] font-semibold uppercase tracking-wide text-orange-700 bg-orange-50 border border-orange-200 rounded-full px-2 py-0.5">Focused review</span>}
        </div>
        <div className="text-xs text-slate-400">{audit.subject_type === 'fdr' ? 'FDR' : 'Internal'} · {audit.subject_name} · {audit.criteria?.length || 0} criteria</div>
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-500 mt-1">
          {audit.period_start && <span>Period under review: <b>{fmtHeaderDate(audit.period_start)}</b> to <b>{fmtHeaderDate(audit.period_end) || '…'}</b></span>}
          {audit.engagement_date && <span>Engagement date: <b>{fmtHeaderDate(audit.engagement_date)}</b></span>}
          {audit.evidence_due_date && <span>Evidence due: <b>{fmtHeaderDate(audit.evidence_due_date)}</b></span>}
        </div>
        {Array.isArray(audit.authorities) && audit.authorities.length > 0 && <div className="text-[11px] text-slate-400 mt-0.5">Authorities: {audit.authorities.map(a => a.name).join('; ')}</div>}
        {audit.stage !== 'closed' && !isFocused && (
          <label className="inline-flex items-center gap-2 text-[11px] text-slate-500 mt-1.5 cursor-pointer select-none">
            <input type="checkbox" checked={!!audit.data_integrity_applicable} disabled={busy === 'ditoggle'} onChange={e => toggleDataCheck(e.target.checked)} />
            This audit includes a data universe check
          </label>
        )}
      </div>

      {/* The audit workspace is a no-PHI zone: reminder + Report PHI purge path. */}
      <PhiNotice auditId={auditId} />

      {/* The spine of the workspace: real stages, the one legal next move,
          honest blockers, and what to do right now in plain words. */}
      <WorkflowBar auditId={auditId} workflow={workflow} counts={counts} audit={audit} onChanged={load} onWrapUp={confirmClose} />

      {error && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      {/* Workspace: the phase rail on the left, the active phase (or the binder)
          in the center, the copilot and working file docked on the right. On
          smaller screens they stack. Every phase's panels stay MOUNTED — the
          rail toggles visibility only — so a phase switch never loses state. */}
      <div className="grid xl:grid-cols-[236px,minmax(0,1fr),340px] gap-4 items-start">

      <PhaseRail currentPhase={currentPhase} activeView={activeView} onSelect={setActiveView}
        isFocused={isFocused} statuses={railStatuses} binderCount={binderCount} />

      <div className="space-y-4 min-w-0">

      {/* The binder: the audit's folder-organized record room, spanning every
          phase — disclosed issues, universe results, data integrity, review
          areas, notices. A first-class destination selected from the rail. */}
      <div hidden={activeView !== 'binder'}>
        <BinderPanel audit={audit} auditId={auditId} />
      </div>

      {/* ── Phase I — Engagement & Universe Submission ── */}
      <div hidden={activeView !== 'p1'}>
      <PhaseSection railMode number="I" lean={isFocused}
        title={isFocused ? 'Engagement' : 'Engagement & Universe Submission'}
        explainer={isFocused ? 'Review the program the agent built, then review and send the engagement notice.' : 'Notify the auditee, collect the universes, test their integrity, and pull the samples.'}
        current={currentPhase === 1}>

      {/* Audit program (visible before fieldwork) */}
      <ProgramPanel criteria={audit.criteria || []} />

      {/* Data request (universes + record layouts) when a protocol drives the audit */}
      {audit.data_request && <DataRequestPanel dataRequest={audit.data_request} schedule={audit.schedule} />}

      {/* Kickoff: the engagement notice — edit, save, rebuild, send. */}
      <EngagementNoticePanel audit={audit} auditId={auditId} onChanged={load} />

      {/* Pre-audit disclosed issues now live in the binder's first folder. */}

      {/* Data check: the universe goes through the Scrubber, right here. */}
      {audit.data_integrity_applicable && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-800">Data check <span className="text-slate-400 font-normal">· powered by the Scrubber</span></div>
              <div className="text-[11px] text-slate-400">Before testing starts, the auditee's data universe is checked for completeness, layout, and timeliness.</div>
            </div>
            <div className="flex gap-2 items-center">
              {universeTypeOptions(audit).length > 0 && (
                <select value={uniType} onChange={e => setUniType(e.target.value)}
                  title="Tell the checker which protocol table this file is — the same choice the Scrubber asks for. Auto-detect works when the file name or headers make it obvious."
                  className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700 max-w-[220px]">
                  <option value="">Which table? (auto-detect)</option>
                  {universeTypeOptions(audit).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              )}
              <input ref={uniRef} type="file" accept=".xlsx,.xls,.csv,.txt" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) validateUni(f); e.target.value = ''; }} />
              <button onClick={() => uniRef.current?.click()} disabled={busy === 'uni'} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">{busy === 'uni' ? 'Checking the file…' : '↑ Check a universe file'}</button>
            </div>
          </div>
          <PhiUploadNotice />
          {(() => {
            const di = audit.data_integrity;
            const st = audit.data_integrity_status || 'pending';
            const stCfg = { validated: ['Looks good', 'bg-emerald-50 text-emerald-700 border-emerald-200'], issues_found: ['Issues found', 'bg-amber-50 text-amber-700 border-amber-200'], failed: ['Not usable yet', 'bg-red-50 text-red-700 border-red-200'], pending: ['Not checked yet', 'bg-slate-50 text-slate-500 border-slate-200'] }[st] || ['Not checked yet', 'bg-slate-50 text-slate-500 border-slate-200'];
            return (
              <>
                <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
                  <span className={`px-2 py-0.5 rounded-full font-semibold border text-[11px] ${stCfg[1]}`}>{stCfg[0]}</span>
                  {di?.file_name && <span>{di.file_name}</span>}
                  {di?.metrics?.universe_type && <span className="font-mono text-[11px]">{di.metrics.universe_type}</span>}
                  {di?.metrics?.total_rows != null && <span>{di.metrics.total_rows} rows · {di.metrics.rows_with_issues ?? 0} with issues{di.metrics.error_rate ? ` (${di.metrics.error_rate})` : ''}</span>}
                </div>
                {di?.summary && <div className="text-[12px] text-slate-600">{di.summary}</div>}
                {di?.metrics?.category_counts && Object.keys(di.metrics.category_counts).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(di.metrics.category_counts).map(([k, v]) => (
                      <span key={k} className="text-[10px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{k}: <b>{v}</b></span>
                    ))}
                  </div>
                )}
                {Array.isArray(di?.recommendations) && di.recommendations.length > 0 && (
                  <details className="text-[12px] text-slate-500">
                    <summary className="cursor-pointer font-semibold text-slate-400">What to fix ({di.recommendations.length})</summary>
                    <ul className="list-disc list-inside mt-1 space-y-0.5">{di.recommendations.slice(0, 10).map((r, i) => <li key={i}>{r}</li>)}</ul>
                  </details>
                )}
                {!di && <div className="text-xs text-slate-400">Upload the auditee's universe file (.xlsx, .csv, or .txt). It runs through the Scrubber's CMS record-layout, field, and timeliness checks and the result lands here — no app switching.</div>}
                <div className="flex gap-2 items-center pt-1">
                  <span className="text-[10px] uppercase tracking-wide text-slate-400">Reviewer decision</span>
                  <button onClick={() => saveDI('validated')} disabled={busy === 'di'} className="text-[11px] font-semibold rounded-lg bg-emerald-600 text-white px-2.5 py-1">Accept &amp; continue</button>
                  <button onClick={() => saveDI('failed')} disabled={busy === 'di'} className="text-[11px] font-semibold rounded-lg border border-slate-300 text-slate-600 px-2.5 py-1">Send back to auditee</button>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* CMS three-attempt universe rule — program audit only */}
      {!isFocused && <UniverseAttemptsPanel audit={audit} auditId={auditId} onChanged={load} />}

      {/* Sample selection from the submitted universe — program audit only */}
      {!isFocused && <SamplesPanel auditId={auditId} />}

      </PhaseSection>
      </div>{/* /phase I */}

      {/* ── Phase II — Field Work ── */}
      <div hidden={activeView !== 'p2'}>
      <PhaseSection railMode number="II" lean={isFocused} title="Fieldwork"
        explainer={isFocused ? 'Upload the evidence and run the grounded review — every conclusion quote-backed for you to confirm.' : 'Hold the conferences, test the sampled cases against the program, and request root cause and impact analyses when issues surface.'}
        current={currentPhase === 2}>

      {/* Entrance / status / exit conferences — program audit only */}
      {!isFocused && <ConferencesPanel auditId={auditId} />}

      {/* Fieldwork: evidence + run */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <PhiUploadNotice />
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-slate-800">Fieldwork</div>
            <div className="text-[11px] text-slate-400">Evidence is collected and every tested condition is scored against it. Uploads are scanned; PHI is quarantined automatically.</div>
          </div>
          <div className="flex gap-2 items-center">
            <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.md,.csv" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
            <button onClick={() => fileRef.current?.click()} disabled={busy === 'up'} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">{busy === 'up' ? 'Uploading…' : '↑ Upload evidence'}</button>
            <button onClick={run} disabled={busy === 'run' || isRunning || !documents.length} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50 flex flex-col items-center leading-tight">
              <span>{isRunning ? 'Reviewing…' : (audit.status === 'completed' ? 'Re-run grounded review' : 'Run grounded review')}</span>
              <span className="text-[9px] font-normal opacity-80">AI review with adversarial verification</span>
            </button>
            {!agentGone && !isFocused && (
              <button onClick={runAgent} disabled={busy === 'agent' || isRunning || !documents.length} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 disabled:opacity-50 flex flex-col items-center leading-tight">
                <span>{isRunning ? 'Working…' : 'Run fieldwork agent'}</span>
                <span className="text-[9px] font-normal opacity-80 max-w-[220px]">Works the samples like an auditor — every conclusion quote-backed, everything held for your review</span>
              </button>
            )}
          </div>
        </div>
        {documents.length > 0
          ? <div className="text-xs text-slate-500">Evidence: {documents.map(d => <span key={d.id} className="mr-2 text-slate-700">{d.file_name}{d.extraction_status !== 'completed' ? ` (${d.extraction_status})` : ''}</span>)}</div>
          : <div className="text-xs text-slate-400">Upload the evidence (policies, files, logs, attestations). The grounded review reads it and scores each criterion with a verbatim quote as proof.</div>}
        {isRunning && (
          <div className="bg-slate-50 rounded-xl p-3">
            <div className="flex items-center justify-between text-xs text-slate-600 mb-1"><span>{audit.progress_msg || 'Auditing…'}</span><span>{audit.progress_pct || 0}%</span></div>
            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-orange-500 to-orange-500 rounded-full transition-all" style={{ width: `${audit.progress_pct || 0}%` }} /></div>
          </div>
        )}
        {audit.status === 'failed' && <div className="text-xs text-red-600">Audit failed: {audit.error_message}. You can re-run it.</div>}
      </div>

      {/* Results */}
      {audit.status === 'completed' && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4 flex-wrap bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-4 text-xs text-slate-600">
              <span><b className="text-emerald-600">{audit.criteria_met}</b> met</span>
              <span><b className="text-amber-600">{audit.criteria_deficient}</b> deficient</span>
              <span><b className="text-red-600">{audit.criteria_missing}</b> missing</span>
              {audit.criteria_unverified > 0 && <span><b className="text-indigo-600">{audit.criteria_unverified}</b> needs review</span>}
              <span className="text-slate-400">of {audit.criteria_total}</span>
            </div>
            <div className="flex items-center gap-3">
              {posture && <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${posture.color}`}>{posture.label}</span>}
              {audit.compliance_score != null && <div className="text-right"><div className="text-2xl font-bold text-slate-800 leading-none">{audit.compliance_score}%</div><div className="text-[10px] text-slate-400 uppercase">weighted</div></div>}
            </div>
          </div>

          {audit.overall_opinion && (
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Auditor opinion</div>
              <p className="text-sm text-slate-700 leading-relaxed">{audit.overall_opinion}</p>
              {actions.length > 0 && <ol className="list-decimal list-inside mt-3 space-y-1 text-sm text-slate-700">{actions.map((a, i) => <li key={i}>{a}</li>)}</ol>}
            </div>
          )}

          {promotable > 0 && (
            <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
              <div className="text-sm text-slate-700">{promotable} deficiency{promotable === 1 ? '' : 'ies'} not yet raised as findings.</div>
              <button onClick={promote} disabled={busy === 'promote'} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-orange-600 text-white shadow disabled:opacity-50">{busy === 'promote' ? 'Raising…' : (audit.subject_type === 'fdr' ? 'Raise findings (→ FDR Findings & CAPs)' : 'Raise findings & CAPs')}</button>
            </div>
          )}

          <div className="space-y-2">{results.map(r => <CriterionRow key={r.id} r={r} auditId={auditId} onChanged={load} />)}</div>

          <p className="text-[11px] text-slate-400 italic">Decision support, not a final determination. Scores are grounded in the evidence; a qualified auditor must review and confirm. Items marked "needs review" require a human decision.</p>
        </div>
      )}

      {/* Requests to the auditee: RCA / impact / document requests / questions
          — the CMS request apparatus is program-audit only */}
      {!isFocused && <AnalysisRequestsPanel auditId={auditId} findings={findings} documents={documents} onEmail={emailAnalysis} />}

      </PhaseSection>
      </div>{/* /phase II */}

      {/* ── Phase III — Reporting ── */}
      <div hidden={activeView !== 'p3'}>
      <PhaseSection railMode number="III" lean={isFocused} title="Reporting"
        explainer={isFocused ? 'Assemble the report, raise any findings, and open a CAP if corrective action is needed.' : 'Issue the preliminary report, take the auditee\'s comments on the draft, classify every condition, and issue the final report.'}
        current={currentPhase === 3}>

          {inReporting && audit.status !== 'completed' && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-6 text-center text-sm text-slate-500">
              Fieldwork was performed outside the grounded review — the report will carry your findings and notes.
            </div>
          )}

          {/* The CMS report cycle: preliminary → draft → final, each version
              frozen at issuance. Absorbs the old single report bar. */}
          <ReportCyclePanel auditId={auditId} onEmail={emailReport} onChanged={load} />

          {findings.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Findings & corrective action plans</div>
                  <div className="text-[11px] text-slate-400">Deficiencies raised formally, each tracked to a corrective action.</div>
                </div>
                {audit.created_cap_id
                  ? <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">CAP open in the CAP module</span>
                  : <button onClick={createCap} disabled={busy === 'cap'} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white shadow disabled:opacity-50">{busy === 'cap' ? 'Opening…' : 'Open a CAP for these findings'}</button>}
              </div>
              {findings.map(f => <FindingCard key={f.id} f={f} auditId={auditId} onChanged={load} />)}
            </div>
          )}

      </PhaseSection>
      </div>{/* /phase III */}

      {/* ── Phase IV — Validation & Close Out ── */}
      <div hidden={activeView !== 'p4'}>
      <PhaseSection railMode number="IV" lean={isFocused} title={isFocused ? 'Close out' : 'Validation & Close Out'}
        explainer={isFocused ? 'Confirm any corrective actions landed, then close the record.' : 'Validate that corrective actions actually fixed the CARs, then close the audit record.'}
        current={currentPhase === 4}>

          <ValidationPanel findings={findings} auditId={auditId} onChanged={load} />

          {audit.stage !== 'closed'
            ? (inReporting
                ? <button onClick={confirmClose} disabled={busy === 'close' || busy === 'close-preview'}
                    title="Closing destroys all PHI held for this audit — files, extracted text, and sample details. Findings, file names, and pass/fail records are kept."
                    className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-slate-300 text-slate-600">Wrap up this audit</button>
                : <div className="text-xs text-slate-400">Wrapping up unlocks once the audit reaches Report & findings.</div>)
            : <div className="text-sm text-emerald-700">Wrapped up{audit.closed_at ? ` ${new Date(audit.closed_at).toLocaleDateString()}` : ''}.{audit.coa_activity_id ? ' The work plan has been updated.' : ''}</div>}

          {/* The certificate: closing destroyed the PHI, and here is the proof. */}
          {audit.stage === 'closed' && audit.phi_destruction && (
            <div className={`mt-3 rounded-xl border px-4 py-3 ${audit.phi_destruction.verification?.clean ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
              <div className={`text-sm font-bold ${audit.phi_destruction.verification?.clean ? 'text-emerald-800' : 'text-red-800'}`}>
                {audit.phi_destruction.verification?.clean ? 'No PHI remains in this audit — verified.' : 'PHI destruction ran, but verification found leftovers — contact support.'}
              </div>
              <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                Destroyed on {new Date(audit.phi_destruction.destroyed_at).toLocaleString()}: {audit.phi_destruction.destroyed?.documents ?? 0} stored file(s),{' '}
                {audit.phi_destruction.destroyed?.sample_snapshots ?? 0} sample snapshot(s) reduced to case ID + row number,{' '}
                {audit.phi_destruction.destroyed?.notes_erased ?? audit.phi_destruction.destroyed?.notes_masked ?? 0} note(s) and {audit.phi_destruction.destroyed?.quotes_erased ?? audit.phi_destruction.destroyed?.quotes_masked ?? 0} evidence quote(s) erased.
                {' '}File names and fingerprints are retained on the audit trail.
              </div>
            </div>
          )}

          {/* Close confirmation: the honest prompt, with real counts. */}
          {closePreview && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setClosePreview(null)}>
              <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-slate-800">Wrap up and destroy PHI?</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                  Closing this audit permanently destroys the protected health information it holds. This cannot be undone.
                </p>
                <ul className="mt-3 text-sm text-slate-700 space-y-1.5">
                  <li>• <span className="font-semibold">{closePreview.stored_files ?? 0} stored file(s)</span> erased{closePreview.example_file_names?.length ? ` (${closePreview.example_file_names.slice(0, 3).join(', ')}${(closePreview.stored_files ?? 0) > 3 ? ', …' : ''})` : ''}</li>
                  <li>• <span className="font-semibold">{closePreview.sample_snapshots ?? 0} sample snapshot(s)</span> reduced to case ID + row number</li>
                  <li>• <span className="font-semibold">{closePreview.notes_to_mask ?? 0} note(s)</span> and all evidence quotes erased</li>
                </ul>
                <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                  Kept forever: the engagement letter, findings and their classifications, which samples passed or failed, every file's name and fingerprint, and deficiency descriptions. A destruction certificate is written to the audit trail.
                </p>
                <div className="flex justify-end gap-2 mt-5">
                  <button onClick={() => setClosePreview(null)} className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-600">Cancel</button>
                  <button onClick={close} disabled={busy === 'close'}
                    className="px-4 py-1.5 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                    {busy === 'close' ? 'Destroying…' : 'Close audit & destroy PHI'}
                  </button>
                </div>
              </div>
            </div>
          )}

      </PhaseSection>
      </div>{/* /phase IV */}

      </div>{/* /center stage column */}

      {/* Right rail: the copilot, the working file, the outbox, and the
          activity trail travel with the audit. */}
      <div className="space-y-4 xl:sticky xl:top-4">
        <CopilotPanel auditId={auditId} stage={audit.stage} />
        <AuditTeamPanel auditId={auditId} audit={audit} onChanged={load} />
        <IntakeDrawer auditId={auditId} />
        <AuditeeAccessPanel auditId={auditId} audit={audit} />
        <CorrespondenceCard auditId={auditId} refreshKey={corrKey} onCompose={() => setCompose({})} />
        <ActivityTrail auditId={auditId} refreshKey={loadCount + corrKey} />
      </div>

      </div>{/* /workspace grid */}

      {compose && (
        <ComposeDialog auditId={auditId} initial={compose} onClose={() => setCompose(null)} onSent={onCorrespondenceSent} />
      )}
    </div>
  );
}

// ── Teach a new protocol: create the shell, then learn from its source ──
function ProtocolTeach({ onBack, onCreated }) {
  const [name, setName] = useState('');
  const [program, setProgram] = useState('');
  const [authorities, setAuthorities] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const create = async () => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/api/audit/protocols`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, program: program || null, authorities }) });
      const d = await res.json(); if (!d.success) throw new Error(d.message || 'Could not create protocol');
      onCreated(d.protocol.id);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 max-w-2xl">
      <div className="flex items-center justify-between"><div className="text-sm font-semibold text-slate-800">Teach a new protocol</div><button onClick={onBack} className="text-xs text-slate-500">Cancel</button></div>
      <p className="text-[12px] text-slate-500">Name the audit type, then on the next step upload its governing document (a CMS program-audit protocol, an NCQA standard, a state model contract, a regulation). The platform reads it once, extracts the universes, record layouts, review period, and tested conditions, and grounds every condition in a verbatim quote from the source. You approve it once; after that every audit of this type reuses it.</p>
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Protocol name (e.g., SARAG, Part C UM, NCQA UM delegation)" className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2" />
      <select value={program} onChange={e => setProgram(e.target.value)} className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 text-slate-700">
        <option value="">Program (optional)…</option>
        <option value="medicare_advantage">Medicare Advantage</option>
        <option value="part_d">Part D</option>
        <option value="medicaid">Medicaid</option>
        <option value="dual_dsnp">Dual / D-SNP</option>
        <option value="other">Other</option>
      </select>
      <AuthoritiesEditor value={authorities} onChange={setAuthorities} />
      {err && <div className="text-xs text-red-600">{err}</div>}
      <button onClick={create} disabled={busy || !name.trim()} className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">{busy ? 'Creating…' : 'Continue to upload source →'}</button>
    </div>
  );
}

// ── Review a learned protocol: upload source, learn, check grounding, approve ──
function ProtocolReview({ id, onBack }) {
  const [p, setP] = useState(null);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await fetch(`${API}/api/audit/protocols/${id}`, { credentials: 'include' }).then(r => r.json()); if (r.success) { setP(r.protocol); setDocs(r.documents || []); } } catch {}
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (p?.learn_status !== 'learning') return; const t = setInterval(load, 3000); return () => clearInterval(t); }, [p?.learn_status, load]);

  const act = async (fn, key) => { setBusy(key); setErr(''); try { await fn(); } catch (e) { setErr(e.message); } setBusy(''); };
  const upload = (file) => act(async () => { const fd = new FormData(); fd.append('file', file); const r = await fetch(`${API}/api/audit/protocols/${id}/documents`, { method: 'POST', credentials: 'include', body: fd }).then(r => r.json()); if (!r.success) throw new Error(r.message); await load(); }, 'up');
  const learn = () => act(async () => { const r = await fetch(`${API}/api/audit/protocols/${id}/learn`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' }).then(r => r.json()); if (!r.success) throw new Error(r.message); await load(); }, 'learn');
  const approve = () => act(async () => { const r = await fetch(`${API}/api/audit/protocols/${id}/approve`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' }).then(r => r.json()); if (!r.success) throw new Error(r.message); await load(); }, 'approve');
  const removeCriterion = (key) => act(async () => {
    const next = (p.criteria || []).filter(c => c.key !== key);
    const r = await fetch(`${API}/api/audit/protocols/${id}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ criteria: next }) }).then(r => r.json());
    if (!r.success) throw new Error(r.message); await load();
  }, 'edit');

  if (!p) return <div className="text-sm text-slate-400 py-6">Loading protocol…</div>;
  const ung = new Set(p.grounding?.ungrounded || []);
  const learning = p.learn_status === 'learning';

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between"><button onClick={onBack} className="text-sm text-slate-500">← Protocols</button>
        {p.status === 'approved' ? <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Approved · ready to use</span>
          : <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Draft</span>}
      </div>
      <div className="text-base font-bold text-slate-800">{p.name}</div>
      {err && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</div>}

      {/* Source documents */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-800">Source document(s)</div>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.md" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
            <button onClick={() => fileRef.current?.click()} disabled={busy === 'up'} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">{busy === 'up' ? 'Uploading…' : '↑ Upload source'}</button>
            {docs.length > 0 && !learning && <button onClick={learn} disabled={busy === 'learn'} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">{p.learn_status === 'learned' ? 'Re-learn' : 'Learn from source'}</button>}
          </div>
        </div>
        {docs.length > 0
          ? <div className="text-xs text-slate-500">{docs.map(d => <span key={d.id} className="mr-2 text-slate-700">{d.file_name}{d.extraction_status !== 'completed' ? ` (${d.extraction_status})` : ''}</span>)}</div>
          : <div className="text-xs text-slate-400">Upload the governing document. PDF, Word, or text.</div>}
        {p.learn_error && <div className="text-xs text-red-600">Learning failed: {p.learn_error}</div>}
      </div>

      {learning && (
        <div className="bg-slate-50 rounded-xl p-3">
          <div className="flex items-center justify-between text-xs text-slate-600 mb-1"><span>{p.learn_progress_msg || 'Learning…'}</span><span>{p.learn_progress_pct || 0}%</span></div>
          <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-orange-500 to-orange-500 rounded-full transition-all" style={{ width: `${p.learn_progress_pct || 0}%` }} /></div>
        </div>
      )}

      {p.learn_status === 'learned' && (
        <>
          {p.grounding && (
            <div className={`text-xs rounded-lg px-3 py-2 border ${p.grounding.coverage >= 80 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
              {p.grounding.verified_count} of {p.grounding.total_count} conditions are grounded in a verbatim quote from the source ({p.grounding.coverage}%). {ung.size > 0 && 'Conditions that could not be grounded are flagged below — review or remove them before approving.'}
            </div>
          )}
          {p.protocol_spec?.universes?.length > 0 && <DataRequestPanel dataRequest={{ universes: p.protocol_spec.universes, sample_plan: p.protocol_spec.sample_plan, submission: { rules: p.protocol_spec.data_request?.rules } }} />}
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
            <div className="text-sm font-semibold text-slate-800">Tested conditions ({(p.criteria || []).length})</div>
            {(p.criteria || []).map((c) => (
              <div key={c.key} className="border border-slate-100 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${SEV_CFG[c.severity] || SEV_CFG.low}`}>{c.severity}</span>
                  <span className="text-sm font-semibold text-slate-800">{c.title}</span>
                  {ung.has(c.key) ? <span className="text-[10px] font-semibold text-red-600 bg-red-50 rounded px-1.5 py-0.5">not grounded</span> : <span className="text-[10px] text-emerald-600">✓ grounded</span>}
                  <button onClick={() => removeCriterion(c.key)} disabled={busy === 'edit'} className="ml-auto text-[11px] text-slate-400 hover:text-red-600">remove</button>
                </div>
                {c.citation && <div className="text-[11px] font-mono text-slate-400 mt-0.5">{c.citation}</div>}
                {c.authority && <blockquote className="border-l-2 border-slate-200 pl-2 mt-1 text-[12px] italic text-slate-500">{c.authority}</blockquote>}
              </div>
            ))}
            {p.status !== 'approved' && (
              <button onClick={approve} disabled={busy === 'approve' || !(p.criteria || []).length} className="mt-2 px-3 py-1.5 text-sm font-semibold rounded-lg bg-emerald-600 text-white shadow disabled:opacity-50">{busy === 'approve' ? 'Approving…' : 'Approve protocol'}</button>
            )}
          </div>
          <p className="text-[11px] text-slate-400 italic">The extraction is grounded in the source; a qualified auditor reviews and approves. Approving makes this protocol reusable for every future audit of this type at no additional model cost.</p>
        </>
      )}
    </div>
  );
}

// ── Protocols manager ──
function ProtocolsManager({ onClose }) {
  const [data, setData] = useState({ pinned: [], learned: [] });
  const [sel, setSel] = useState(null);
  const [teaching, setTeaching] = useState(false);
  const load = useCallback(async () => {
    try { const r = await fetch(`${API}/api/audit/protocols`, { credentials: 'include' }).then(r => r.json()); if (r.success) setData({ pinned: r.pinned || [], learned: r.learned || [] }); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  if (teaching) return <ProtocolTeach onBack={() => setTeaching(false)} onCreated={(id) => { setTeaching(false); setSel(id); }} />;
  if (sel) return <ProtocolReview id={sel} onBack={() => { setSel(null); load(); }} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={onClose} className="text-sm text-slate-500">← Audits</button>
        <button onClick={() => setTeaching(true)} className="px-3 py-2 text-sm font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow">+ Teach a new protocol</button>
      </div>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">CMS protocols (ready to use)</div>
        {data.pinned.map(p => (
          <div key={p.key} className="w-full flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 mb-2">
            <div><div className="text-sm font-semibold text-slate-800">{p.name}</div><div className="text-[11px] text-slate-400">{p.universe_count} universes · {p.criteria_count} conditions{p.enrollment_tiered ? ' · enrollment-tiered review period' : ''}</div></div>
            <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">pinned</span>
          </div>
        ))}
      </div>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Your learned protocols</div>
        {data.learned.length === 0 && <div className="text-sm text-slate-400 bg-slate-50 border border-dashed border-slate-200 rounded-xl px-4 py-6 text-center">None yet. Click "Teach a new protocol" and upload its source document. It is learned once and you reuse it forever.</div>}
        {data.learned.map(p => (
          <button key={p.id} onClick={() => setSel(p.id)} className="w-full flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 mb-2 text-left hover:border-orange-200">
            <div><div className="text-sm font-semibold text-slate-800">{p.name}</div><div className="text-[11px] text-slate-400">{p.criteria_count} conditions{p.grounding ? ` · ${p.grounding.coverage}% grounded` : ''}{p.learn_status === 'learning' ? ` · learning ${p.learn_progress_pct || 0}%` : ''}</div></div>
            <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 border ${p.status === 'approved' ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-amber-700 bg-amber-50 border-amber-200'}`}>{p.status === 'approved' ? 'approved' : (p.learn_status || 'draft')}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main app ──
// Department-bound users (detected the same way the Sentinel shell does:
// not can_see_privacy) get ONLY the "My audits" request-and-respond surface —
// no plan, no library, no workbench. Compliance users get the full module
// unchanged. Callers that don't pass a user (the FDR tab embed) are
// compliance-gated upstream and render the workbench.
export default function AuditApp({ scope = 'all', user = null }) {
  const deptBound = !!user && user.can_see_privacy !== true;
  // Program audits (ODAG first) are the internal front door — the audit run
  // the CMS way: universes, Scrubber, case testing, certified purge. The
  // prior workbench stays reachable for audits already underway there.
  const [mode, setMode] = useState('program'); // program | workbench
  if (deptBound) return <MyAudits />;
  if (scope !== 'fdr' && mode === 'program') {
    return (
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="px-8 pt-3 flex items-center flex-none">
          <span className="text-[15px] font-bold text-slate-900">Audits</span>
          <span className="ml-2 text-[11px] text-slate-400">CMS · Internal</span>
          <button onClick={() => setMode('workbench')}
            className="ml-auto text-[11.5px] font-semibold text-slate-400 hover:text-slate-600">
            Earlier audits (previous workspace)
          </button>
        </div>
        <ProgramAudits />
      </div>
    );
  }
  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {scope !== 'fdr' && (
        <div className="px-8 pt-3 flex-none">
          <button onClick={() => setMode('program')} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-700">← Audits</button>
        </div>
      )}
      <AuditWorkbench scope={scope} />
    </div>
  );
}

function AuditWorkbench({ scope = 'all' }) {
  const [audits, setAudits] = useState([]);
  const [fdrEntities, setFdrEntities] = useState([]);
  const [builtins, setBuiltins] = useState([]);
  const [custom, setCustom] = useState([]);
  const [protocols, setProtocols] = useState({ pinned: [], learned: [] });
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  // The year's plan is the front door for the standalone app; the FDR tab
  // keeps its focused audit list.
  const [view, setView] = useState('plan'); // plan | audits | reporting | protocols
  const [loading, setLoading] = useState(true);

  const loadAudits = useCallback(async () => {
    try {
      // FDR audits live in the FDR app. The Audit app only ever lists internal audits.
      const qs = scope === 'fdr' ? '?subject_type=fdr' : '?subject_type=internal';
      const res = await fetch(`${API}/api/audit/audits${qs}`, { credentials: 'include' });
      const d = await res.json(); if (d.success) setAudits(d.audits || []);
    } catch {}
  }, [scope]);

  const loadProtocols = useCallback(async () => {
    try { const r = await fetch(`${API}/api/audit/protocols`, { credentials: 'include' }).then(r => r.json()); if (r.success) setProtocols({ pinned: r.pinned || [], learned: r.learned || [] }); } catch {}
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [t, e] = await Promise.all([
          fetch(`${API}/api/audit/templates`, { credentials: 'include' }).then(r => r.json()),
          scope === 'fdr' ? fetch(`${API}/api/fdr/entities`, { credentials: 'include' }).then(r => r.json()).catch(() => null) : Promise.resolve(null),
        ]);
        if (t.success) { setBuiltins(t.builtins || []); setCustom(t.custom || []); }
        if (e) setFdrEntities(Array.isArray(e) ? e : (e.entities || []));
      } catch {}
      setLoading(false);
    })();
    loadAudits();
    loadProtocols();
  }, [loadAudits, loadProtocols, scope]);

  if (loading) return <div className="p-8 text-sm text-slate-400">Loading…</div>;

  const tabBtn = (key, label) => (
    <button onClick={() => setView(key)} className={`px-3 py-1.5 text-sm font-semibold rounded-lg ${view === key ? 'bg-orange-100 text-orange-700' : 'text-slate-500 hover:bg-slate-100'}`}>{label}</button>
  );

  const body = (
    <div className="max-w-6xl mx-auto space-y-5">
      {openId ? (
        <AuditDetail auditId={openId} onBack={() => { setOpenId(null); loadAudits(); }} />
      ) : view === 'protocols' ? (
        <ProtocolsManager onClose={() => { setView(scope === 'fdr' ? 'audits' : 'plan'); loadProtocols(); }} />
      ) : creating ? (
        <SetupForm scope={scope} fdrEntities={fdrEntities} builtins={builtins} custom={custom} protocols={protocols}
          onManageProtocols={() => setView('protocols')}
          onCreated={(id) => { setCreating(false); setOpenId(id); loadAudits(); }} onCancel={() => setCreating(false)} />
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex gap-1">{tabBtn('plan', "This year's plan")}{tabBtn('audits', 'All audits')}{tabBtn('reporting', 'Reporting')}</div>
            <div className="flex gap-2">
              <button onClick={() => { setView('protocols'); }} className="px-3 py-2 text-sm font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">Library</button>
              <button onClick={() => { loadProtocols(); setCreating(true); }} className="px-3 py-2 text-sm font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow">+ New audit</button>
            </div>
          </div>
          {view === 'plan' ? (
            scope === 'fdr'
              ? <FdrPlanView onOpenAudit={(id) => { setOpenId(id); loadAudits(); }} />
              : <PlanView onOpenAudit={(id) => { setOpenId(id); loadAudits(); }} />
          ) : view === 'reporting' ? (
            <ReportingView lockedSubjectType={scope === 'fdr' ? 'fdr' : 'internal'} />
          ) : (
          <>
          {audits.length === 0 && <div className="text-sm text-slate-400 bg-slate-50 border border-dashed border-slate-200 rounded-xl px-4 py-8 text-center">No audits yet. Click "New audit" to run one from a CMS protocol, a learned protocol, or a template.</div>}
          {audits.map(a => {
            const posture = a.compliance_posture ? (POSTURE_CFG[a.compliance_posture] || POSTURE_CFG.not_assessable) : null;
            return (
              <button key={a.id} onClick={() => setOpenId(a.id)} className="w-full flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 text-left hover:border-orange-200">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{a.template_name} <span className="text-slate-400 font-normal">· {a.subject_name}</span></div>
                  <div className="text-[11px] text-slate-400">{a.subject_type === 'fdr' ? 'FDR' : 'Internal'} · {STAGE_LABELS[a.stage] || a.stage} · {new Date(a.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {RUNNING.has(a.status) && <span className="text-xs text-amber-600">{a.progress_pct || 0}%</span>}
                  {a.status === 'completed' && posture && <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${posture.color}`}>{posture.label}</span>}
                  {a.status === 'completed' && a.compliance_score != null && <span className="text-sm font-bold text-slate-700">{a.compliance_score}%</span>}
                </div>
              </button>
            );
          })}
          </>
          )}
        </>
      )}
    </div>
  );

  // Embedded (FDR tab) renders bare; standalone renders with a header.
  if (scope === 'fdr') return <div className="p-8">{body}</div>;
  return (
    <div className="h-full flex flex-col bg-[#f8fafc]">
      <header className="flex-shrink-0 bg-white border-b border-slate-200/60 px-8 pt-6 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white shadow-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">Audit</h1>
            <p className="text-slate-400 text-sm mt-0.5">Your audit year, from the work plan to the closed CAP — one place, one record</p>
          </div>
        </div>
      </header>
      <main className="flex-1 overflow-auto p-8">{body}</main>
    </div>
  );
}
